/**
 * Public (short) URLs for event registration.
 *
 * Production serves the same app on inscripcion.addbolivia.org (Cloudflare Pages
 * custom domain). On that host the routes are short:
 *   /           → events open for registration
 *   /:code      → registration form
 *   /p/:token   → filled form (badge / receipt QR)
 * VITE_PUBLIC_REGISTRATION_URL (e.g. https://inscripcion.addbolivia.org) makes
 * shared links and QR codes use the short host from any environment; without it
 * links fall back to the current origin + /inscripcion.
 */

const configured = (import.meta.env.VITE_PUBLIC_REGISTRATION_URL as string | undefined)?.replace(/\/+$/, '');

/** True on the short registration host (inscripcion.addbolivia.org, inscripcion-staging.addbolivia.org, …) */
export function isRegistrationHost(hostname = window.location.hostname): boolean {
  return /^inscripcion(-[a-z0-9]+)?\./.test(hostname);
}

/** Base for the events list: https://inscripcion.addbolivia.org or <origin>/inscripcion */
export function registrationBaseUrl(): string {
  if (configured) return configured;
  return isRegistrationHost() ? window.location.origin : `${window.location.origin}/inscripcion`;
}

/** Shareable registration link for an event code */
export function registrationUrl(code: string): string {
  return `${registrationBaseUrl()}/${encodeURIComponent(code)}`;
}

/** In-app path to an event's form (relative to the current host) */
export function registrationPath(code: string): string {
  return isRegistrationHost() ? `/${encodeURIComponent(code)}` : `/inscripcion/${encodeURIComponent(code)}`;
}

/** In-app path to the open-events list */
export function registrationListPath(): string {
  return isRegistrationHost() ? '/' : '/inscripcion';
}

/** URL encoded in badge / form QR codes (short host when configured → smaller, no "admin") */
export function publicFormUrl(token: string): string {
  const origin = configured ? new URL(configured).origin : window.location.origin;
  return `${origin}/p/${token}`;
}

/** Personal edit link an admin sends to a participant (ADB-169). Not the QR token: it expires and can be revoked. */
export function publicEditUrl(token: string): string {
  const origin = configured ? new URL(configured).origin : window.location.origin;
  return `${origin}/editar/${token}`;
}
